import React from 'react';
import type { CalculatorDirection } from '@/api/models';
import { useDistributionQuoteQuery } from '@/api/queries';
import { parseGelToTetri } from '@/utils/money';
import { toQuoteFigures, type QuoteFigures } from '../../dividendsMeta';

type UseExtractionCalculatorParams = {
    /** The as-of date the quote is priced against. */
    asOf: string;
};

/**
 * Logic behind the extraction calculator, so the component stays render-only
 * (the repo's triple pattern).
 *
 * A distribution larger than the entity can currently fund is priced anyway,
 * with the shortfall stated — refusing to answer would make the calculator
 * useless for the question people actually ask.
 */
export const useExtractionCalculator = ({
    asOf,
}: UseExtractionCalculatorParams) => {
    const [direction, setDirection] =
        React.useState<CalculatorDirection>('BY_CHARGE');
    const [amount, setAmount] = React.useState('');

    const trimmed = amount.trim();
    /**
     * The amount was rejected — it is not money the ledger can represent.
     *
     * Judged here, before the request, because the write edge turns an
     * unparseable amount into the number `0`: sent, the server would price a nil
     * distribution and answer ₾0.00, which reads as an arithmetic result rather
     * than a refusal of what was typed.
     *
     * Distinguished from "nothing typed yet" on purpose: both leave `quote`
     * null, and answering a value the user *did* type with "Enter an amount"
     * reads as the calculator having broken.
     */
    const invalidAmount = trimmed !== '' && !parseGelToTetri(trimmed).ok;

    const query = useDistributionQuoteQuery(
        { direction, amount: trimmed },
        asOf,
        trimmed !== '' && !invalidAmount,
    );
    const quote = query.data?.success ? query.data.response.quote : null;

    const figures = React.useMemo<QuoteFigures | null>(
        () => (quote ? toQuoteFigures(quote.totals) : null),
        [quote],
    );

    return {
        direction,
        amount,
        onDirectionChange: setDirection,
        onAmountChange: setAmount,
        quote,
        figures,
        invalidAmount,
        isLoading: query.isLoading,
    };
};

export type ExtractionCalculatorModel = ReturnType<
    typeof useExtractionCalculator
>;
